"use client";

import * as React from "react";
import Image from "next/image";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@workspace/ui/components/carousel";
import ChevronLeft from "@workspace/ui/icons/chevron-left";
import ChevronRight from "@workspace/ui/icons/chevron-right";
import { cn } from "@workspace/ui/lib/utils";

type Slide = {
  title: React.ReactNode;
  body: string;
  mobileBg: string;
};

const BASE =
  "https://cdn.inito.com/inito_website/home/assets/spectral-precision";

const defaultSlides: Slide[] = [
  {
    title: (
      <>
        The most precise hormone readings at home<sup>1</sup>
      </>
    ),
    body: "Powered by Inito Spectral Mapping™ Technology, Inito delivers the most precise fertility hormone readings, outperforming other at-home tests in head-to-head lab testing.",
    mobileBg: `${BASE}/precision_mobile_1.png`,
  },
  {
    title: (
      <>
        4 hormones.
        <br />
        Measured separately
      </>
    ),
    body: "Most monitors test multiple hormones on one track, where one hormone can interfere with another and make results less accurate. Inito measures hormones in four independent tracks, so every reading stays precise.",
    mobileBg: `${BASE}/precision_mobile_2.png`,
  },
  {
    title: "Every hormone shift, precisely measured.",
    body: "The only at-home monitor that double-checks every test with two sensing methods: fluorescence and visual colorimetric imaging. Designed to detect subtle hormone shifts that other tests can miss.",
    mobileBg: `${BASE}/precision_mobile_3.png`,
  },
  {
    title: (
      <>
        Captures 100x
        <br />
        richer data
      </>
    ),
    body: "Inito captures a full 2D image of your entire test surface over time, collecting over 100× richer data from every test. That richer data helps our AI correct for real world variables — delivering lab-grade reliability at home.",
    mobileBg: `${BASE}/precision_mobile_4.png`,
  },
];

function NavArrow({
  direction,
  onClick,
  active,
}: {
  direction: "left" | "right";
  onClick: () => void;
  active: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!active}
      aria-label={direction === "left" ? "Previous slide" : "Next slide"}
      className={cn(
        "flex h-[38px] w-[38px] items-center justify-center rounded-full bg-white/[0.03] backdrop-blur-[25px] transition-opacity",
        active ? "cursor-pointer" : "cursor-default opacity-40",
      )}
    >
      {direction === "left" ? (
        <ChevronLeft className="h-4 w-4 text-[#F3F2F1]" />
      ) : (
        <ChevronRight className="h-4 w-4 text-[#F3F2F1]" />
      )}
    </button>
  );
}

function DotIndicators({
  count,
  current,
  onDotClick,
}: {
  count: number;
  current: number;
  onDotClick: (index: number) => void;
}) {
  if (count <= 1) return null;

  return (
    <div className="rounded-2xl bg-white/10 px-4 py-3">
      <div className="flex items-center gap-[6px]">
        {Array.from({ length: count }).map((_, index) => (
          <button
            key={index}
            type="button"
            onClick={() => onDotClick(index)}
            aria-label={`Go to slide ${index + 1}`}
            className={cn(
              "h-1.5 w-1.5 rounded-full transition-colors duration-200",
              index === current ? "bg-white" : "bg-white/20",
            )}
          />
        ))}
      </div>
    </div>
  );
}

interface SpectralPrecisionCarouselProps {
  slides?: Slide[];
}

export default function SpectralPrecisionCarousel({
  slides = defaultSlides,
}: SpectralPrecisionCarouselProps) {
  const [api, setApi] = React.useState<CarouselApi>();
  const [current, setCurrent] = React.useState(0);
  const [count, setCount] = React.useState(0);

  React.useEffect(() => {
    if (!api) return;

    const sync = () => {
      setCount(api.scrollSnapList().length);
      setCurrent(api.selectedScrollSnap());
    };

    sync();
    api.on("reInit", sync);
    api.on("select", sync);

    return () => {
      api.off("reInit", sync);
      api.off("select", sync);
    };
  }, [api]);

  const scrollPrev = React.useCallback(() => api?.scrollPrev(), [api]);
  const scrollNext = React.useCallback(() => api?.scrollNext(), [api]);
  const scrollTo = React.useCallback(
    (index: number) => api?.scrollTo(index),
    [api],
  );

  return (
    <section className="w-full ">
      <Carousel
        setApi={setApi}
        opts={{ align: "center", loop: false }}
        className="overflow-hidden"
      >
        <CarouselContent className="ml-0">
          {slides.map((slide, index) => (
            <CarouselItem key={index} className="basis-full pl-0">
              <div
                className="relative flex h-[818px] flex-col"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(13, 13, 12, 1) 23%, rgba(53, 53, 52, 1) 100%)",
                }}
              >
                <Image
                  src={slide.mobileBg}
                  alt=""
                  fill
                  className="absolute inset-0 object-cover md:object-contain md:object-top"
                  sizes="(max-width: 500px) 100vw, 500px"
                />

                <div className="h-[100px] md:h-[120px]" />

                <div className="relative flex flex-col gap-6 px-8 py-5">
                  <div className="flex flex-col gap-5">
                    <h3 className="text-center title-32 text-white">
                      {slide.title}
                    </h3>
                    <div className="flex justify-center">
                      <p className="max-w-[400px] text-center body-16 text-[#CECBC9] md:body-18">
                        {slide.body}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex-1" />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>

        <div className="pointer-events-none absolute top-0 right-0 left-0 z-20">
          <div className="pointer-events-auto mx-auto flex w-fit items-center gap-5 px-6 py-8">
            <NavArrow
              direction="left"
              onClick={scrollPrev}
              active={current > 0}
            />
            <DotIndicators
              count={count}
              current={current}
              onDotClick={scrollTo}
            />
            <NavArrow
              direction="right"
              onClick={scrollNext}
              active={current < count - 1}
            />
          </div>
        </div>
      </Carousel>
    </section>
  );
}
